<template>
  <div v-loading="isLoading" class="p-3 flex flex-col bg-key rounded-4xl">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-1 h-9 text-black-white text-l-medium">
        <Icon icon="Technology-Wireframe-FirmwareUpdate" class="size-6" />
        <span class="text-l-medium text-black-white">{{ t('setting.device.firmware.title') }}</span>
      </div>
      <div class="flex items-center gap-2">
        <Button color="black" size="sm" class="h-8" @click="handleOpenUpgradeList">
          {{ t('setting.device.firmware.releaseNotes') }}
        </Button>
        <div class="relative">
          <Button
            color="black"
            size="sm"
            class="h-8"
            :disabled="!canUpdate"
            @click="handleCheckVersion(canUpdate)"
          >
            {{
              !canUpdate
                ? $t('setting.about.update.latestVersion')
                : $t('setting.about.update.checkUpdatesButton')
            }}
          </Button>
          <div
            v-if="canUpdate"
            class="absolute top-0.5 right-0 w-2 h-2 rounded-full bg-brand-red"
          />
        </div>
      </div>
    </div>
    <div class="flex justify-between items-center">
      <span v-show="!hasDongleUpdator" class="text-m-medium text-text-glass-default">
        {{
          t('setting.device.firmware.currentVersion', {
            version: displayVersion,
          })
        }}
      </span>
      <span v-show="hasDongleUpdator" class="text-m-medium text-text-glass-default">
        {{
          t('setting.device.firmware.currentVersion', {
            version: `${firmwareVersion || ''}`,
          })
        }}
      </span>
    </div>
  </div>

  <!-- 最新版本弹窗提示 -->
  <Dialog v-model:open="isLatestVersionTipOpen" content-class="w-90">
    <div class="flex flex-col items-center mb-2">
      <img width="210" height="210" :src="upgradeImg" />
      <p class="w-52.5 text-m-medium text-black-white text-center">
        {{ latestVersionText(canUpdate) }}
      </p>
    </div>
    <template #footer>
      <div class="w-full">
        <Button color="black" class="w-full h-12" @click="isLatestVersionTipOpen = false">
          {{ t('common.confirm') }}
        </Button>
      </div>
    </template>
  </Dialog>

  <!-- 更新固件弹窗 -->
  <Dialog
    v-model:open="isFirmwareUpgradeOpen"
    :title="t('setting.device.firmware.title')"
    content-class="w-90"
  >
    <div>
      <img class="w-full object-contain" :src="bgUrl" />
      <div class="mt-4">
        <p class="text-m-medium text-black-white text-center">
          <template v-if="currentDevice?.getConnectionType() === ConnectionType.Wired">
            {{ t('setting.device.firmware.upgradeWarning') }}
          </template>
          <template v-else>
            <span class="text-text-blue-active">{{
              t('setting.device.firmware.upgradeWiredTip')
            }}</span>
            {{ t('setting.device.firmware.upgradeWarning') }}
          </template>
        </p>
      </div>
      <div class="mt-6 w-full flex items-center gap-3">
        <Button color="text-b-w" class="flex-1 h-10" @click="isFirmwareUpgradeOpen = false">
          {{ t('common.cancel') }}
        </Button>
        <Button color="black" class="flex-1 h-10" @click="handleUpdateFirmware">
          {{ t('common.confirm') }}
        </Button>
      </div>
      <div class="mt-6 flex justify-center">
        <p class="text-m-medium text-text-glass-default">
          {{ t('setting.device.firmware.latestVersion', { version: latestFirmwareVersion }) }}
        </p>
      </div>
    </div>
  </Dialog>

  <!-- 升级进度弹窗由 App 级 FirmwareUpgradeProgressDialog 统一挂载 -->

  <Dialog v-model:open="isUpgradeListOpen" content-class="w-90">
    <template #header>
      <div class="h-7 w-full flex justify-center items-center text-2xl-medium text-black-white">
        {{ t('setting.device.firmware.upgradeLogTitle') }}
      </div>
    </template>
    <ScrollArea class="h-82 pr-2">
      <div class="h-full flex flex-col gap-3">
        <template v-if="upgradeList.length > 0">
          <div v-for="(item, index) in upgradeList" :key="index">
            <div class="flex items-center text-m-medium">
              <span class="text-black-white">{{ item.Version }}</span>
              <span class="text-black-white">（{{ item.Date }}）</span>
            </div>
            <ul class="list-disc ml-6 mt-2">
              <li class="text-s-medium text-text-glass-default">
                {{ language === ZH_CN_LOCALE ? item.DescriptionZh : item.DescriptionEn }}
              </li>
            </ul>
          </div>
        </template>
        <div v-else class="mt-10 flex flex-col items-center justify-center">
          <img :src="emptyImg" width="200px" height="200px" />
          <span class="text-m-medium text-black-white">
            {{ t('setting.device.firmware.upgradeLogEmpty') }}
          </span>
        </div>
      </div>
    </ScrollArea>
    <template #footer>
      <div class="w-full">
        <Button color="black" class="w-full h-12" @click="isUpgradeListOpen = false">
          {{ t('common.confirm') }}
        </Button>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { formatLatestFirmwareVersion } from '@/utils/firmwareVersion'
  import { storeToRefs } from 'pinia'
  import { Button, Icon, Dialog, ScrollArea } from '@rapoo/element'
  import { useDeviceStore, useLocaleStore, useFirmwareStore } from '@/stores'
  import { ConnectionType, VersionType, DeviceType } from '@rapoo/device-sdk'
  import { ZH_CN_LOCALE } from '@/utils/locale'
  import { useDeviceImage } from '@/composables/useDeviceImage'
  import { useFirmwareUpgrade } from '@/composables/useFirmwareUpgrade'

  import upgradeImg from '@/assets/images/devices/upgrade.webp'
  import emptyImg from '@/assets/images/header/settings/empty.webp'

  const firmwareStore = useFirmwareStore()
  const { currentVersions, currentDeviceCanUpdateFirmware } = storeToRefs(firmwareStore)

  const {
    isLoading,
    isLatestVersionTipOpen,
    isFirmwareUpgradeOpen,
    isUpgradeListOpen,
    upgradeList,
    latestVersionText,
    handleCheckVersion,
    handleUpdateFirmware,
    handleOpenUpgradeList,
  } = useFirmwareUpgrade({
    checkWired: true,
  })

  const { t, language } = useLocaleStore()
  const { deviceThumbImage: bgUrl } = useDeviceImage()

  const { currentDevice } = storeToRefs(useDeviceStore())
  const hasDongleUpdator = computed(
    () => currentDevice.value?.getConnectionType() === ConnectionType.Wireless,
  )

  // 鼠标本体固件需按 deviceType='Mouse' 过滤
  const canUpdate = computed<boolean | undefined>(() => {
    if (!currentDeviceCanUpdateFirmware.value) return undefined
    return (
      currentDeviceCanUpdateFirmware.value.filter((item) => item.deviceType === DeviceType.Mouse)
        .length > 0
    )
  })

  const firmwareVersion = computed(() => {
    if (!currentVersions.value) return '-'
    const deviceVersion = currentVersions.value.filter(
      (item) => item.deviceType === DeviceType.Mouse && item.versionType === VersionType.USB,
    )
    return deviceVersion.length > 0 ? deviceVersion[0].version : '-'
  })

  const transparentVersion = computed(() => {
    if (!currentVersions.value) return '-'
    const deviceVersion = currentVersions.value.filter(
      (item) =>
        item.deviceType === DeviceType.Mouse && item.versionType === VersionType.MasterControl,
    )
    return deviceVersion.length > 0 ? deviceVersion[0].version : '-'
  })

  /** 单芯片设备不显示空的第二段版本及其分隔符。 */
  const displayVersion = computed(
    () =>
      [firmwareVersion.value, transparentVersion.value]
        .filter((version) => version !== '-')
        .join(' - ') || '-',
  )
  const latestFirmwareVersion = computed(() =>
    formatLatestFirmwareVersion(currentVersions.value, DeviceType.Mouse),
  )
</script>

<style scoped></style>
